import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Checkbox,
} from '@mui/material';
import { format } from 'date-fns';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import TableHeadCustom from '../../../components/table/table-head-custom';
import TableSelectedAction from '../../../components/table/table-selected-action';
import WfhStatusBadge from './WfhStatusBadge';
import { WFH_STATUS, WFH_HALF_DAY_TYPE_LABELS } from '../constants';

const WfhTable = ({
  wfhRequests,
  onCancelWfh,
  onEditWfh,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  sort = {},
  onSort,
  selectedIds = [],
  selectableIds = [],
  onSelectionChange,
  selectionActions,
}) => {
  const navigate = useNavigate();
  const selectable = Boolean(onSelectionChange);

  // `id` must match the backend `sortBy` values; columns with `render` are not sortable.
  const headLabel = [
    ...(showEmployee ? [{ id: 'employee', label: 'Employee' }] : []),
    { id: 'startDate', label: 'Start Date' },
    { id: 'endDate', label: 'End Date' },
    { id: 'totalDays', label: 'Days' },
    { id: 'halfDay', label: 'Half Day', render: () => 'Half Day' },
    { id: 'status', label: 'Status' },
    ...(showEmployee
      ? [
          { id: 'reviewedBy', label: 'Reviewed By', render: () => 'Reviewed By' },
          { id: 'reviewedAt', label: 'Reviewed Date' },
        ]
      : []),
    { id: 'actions', label: 'Actions', render: () => 'Actions' },
  ];

  const selectableOnPage = (wfhRequests || []).filter((wfh) => selectableIds.includes(wfh.id)).map((wfh) => wfh.id);

  const handleSelectAll = (checked) => onSelectionChange(checked ? selectableOnPage : []);

  const handleSelectRow = (wfhId) =>
    onSelectionChange(
      selectedIds.includes(wfhId) ? selectedIds.filter((id) => id !== wfhId) : [...selectedIds, wfhId]
    );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const getHalfDayLabel = (wfh) => {
    if (!wfh.isHalfDay) return 'Full Day';
    return WFH_HALF_DAY_TYPE_LABELS[wfh.halfDayType] || wfh.halfDayType;
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ position: 'relative' }}>
        {selectable && (
          <TableSelectedAction
            numSelected={selectedIds.length}
            rowCount={selectableOnPage.length}
            onSelectAllRows={handleSelectAll}
            action={selectionActions}
          />
        )}
        <Table>
          <TableHeadCustom
            headLabel={headLabel}
            order={sort.sortOrder}
            orderBy={sort.sortBy}
            onSort={onSort}
            rowCount={selectableOnPage.length}
            numSelected={selectedIds.length}
            onSelectAllRows={selectable ? handleSelectAll : undefined}
          />
          <TableBody>
            {wfhRequests && wfhRequests.length > 0 ? (
              wfhRequests.map((wfh) => (
                <TableRow key={wfh.id} hover selected={selectedIds.includes(wfh.id)}>
                  {selectable && (
                    <TableCell padding="checkbox">
                      {selectableIds.includes(wfh.id) && (
                        <Checkbox checked={selectedIds.includes(wfh.id)} onChange={() => handleSelectRow(wfh.id)} />
                      )}
                    </TableCell>
                  )}
                  {showEmployee && (
                    <TableCell>{wfh.employee?.userName || wfh.employee?.email || '-'}</TableCell>
                  )}
                  <TableCell>{formatDate(wfh.startDate)}</TableCell>
                  <TableCell>{formatDate(wfh.endDate)}</TableCell>
                  <TableCell>{wfh.totalDays}</TableCell>
                  <TableCell>{getHalfDayLabel(wfh)}</TableCell>
                  <TableCell>
                    <WfhStatusBadge status={wfh.status} />
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {wfh.approver && wfh.approver.id === currentUserId
                        ? 'Me'
                        : wfh.approver?.userName || wfh.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(wfh.reviewedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/wfh/${wfh.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {!showEmployee && wfh.status === WFH_STATUS.PENDING && wfh.userId === currentUserId && (
                        <>
                          {checkComponentPermission('UPDATE_WFH') && (
                            <Tooltip title="Edit WFH">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditWfh(wfh.id);
                                }}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {checkComponentPermission('DELETE_WFH') && (
                            <Tooltip title="Delete WFH">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelWfh(wfh.id);
                                }}
                              >
                                <Iconify icon="material-symbols:delete" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headLabel.length + (selectable ? 1 : 0)} align="center">
                  No WFH requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

WfhTable.propTypes = {
  wfhRequests: PropTypes.array.isRequired,
  onCancelWfh: PropTypes.func,
  onEditWfh: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  sort: PropTypes.shape({ sortBy: PropTypes.string, sortOrder: PropTypes.oneOf(['asc', 'desc']) }),
  onSort: PropTypes.func,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  selectableIds: PropTypes.arrayOf(PropTypes.number),
  onSelectionChange: PropTypes.func,
  selectionActions: PropTypes.node,
};

export default WfhTable;
